import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const exportHelper = {
  /**
   * Creates the jspdf doc
   * @param {Array<number>} tableNumbers: ids of the tables to include, or null if tableNumbersWithPrefix are used
   * @param {Array<Object>} tableNumbersWithPrefix:  { tableNumbers: Array<number>, idPrefix: string, groupTitle: string },
   * null if tableNumbers is used, ids of the tables to include, grouped with their prefix and title
   * @param {Object} options:  { title: string, subtitles: Array<string>, orientationPaysage: boolean, reduceHeader: boolean}
   * @returns jspdf doc
   */
  createPdfDoc(tableNumbers, tableNumbersWithPrefix = null, options = null) {
    const maxDimensionCellWidth = 70;
    const totalPagesExp = "{total_pages_count_string}";
    let doc = new jsPDF(options.orientationPaysage ? "l" : "p", "px", "a4");
    doc.page = 1;
    const marginLeft = 20;
    const marginTop = 30;
    // La position du logo cible le coin supérieur gauche, mais pour les textes c'est le bas du texte => compenser en fonction de la taille de la 1ère ligne
    const titleFontSize = options.reduceHeader ? 12 : 20;
    const pageNumberFontSize = options.reduceHeader ? 8 : 11;
    const subtitleFontSize = options.reduceHeader ? 8 : 11;
    const logoPositionY = marginTop - titleFontSize / 2;
    const logoWidth = options.reduceHeader ? 25 : 38;
    const logoHeight = options.reduceHeader ? 22 : 33;
    const titleHeight = options.reduceHeader ? 8 : 12;
    const subtitleUnitHeight = options.reduceHeader ? 7 : 10;
    const verticalSpaceBetweenTables = options.reduceHeader ? 6 : 10;
    const horizontalSpaceBetweenLogoAndTitle = options.reduceHeader ? 6 : 10;
    const pageNumberXStart = options.orientationPaysage ? 570 : 410;

    // Calcul des positions (à faire en amont car la fonction header est exécutée ensuite pour chaque table)
    const titlesTotalTextHeight =
      titleHeight + (subtitleUnitHeight * options.subtitles.length || 0);
    const titlesStartPositionX =
      marginLeft + logoWidth + horizontalSpaceBetweenLogoAndTitle;
    let titlesStartPositionY = marginTop;
    let headerFinalHeight = logoHeight;
    if (titlesTotalTextHeight > logoHeight)
      headerFinalHeight = titlesTotalTextHeight;
    else {
      // Centrage vertical des textes par rapport au logo
      titlesStartPositionY =
        titlesStartPositionY + (logoHeight - titlesTotalTextHeight) / 2;
    }

    let header = function () {
      if (doc.page == doc.internal.getNumberOfPages()) {
        doc.setTextColor(40);
        let img = new Image();
        img.src = "/images/logo.png";
        doc.addImage(
          img,
          "PNG",
          marginLeft,
          logoPositionY,
          logoWidth,
          logoHeight
        );
        let offsetYHeader = titlesStartPositionY;
        doc.setFontSize(titleFontSize);
        doc.text(options.title, titlesStartPositionX, offsetYHeader);
        offsetYHeader = offsetYHeader + titleHeight;

        if (options.subtitles) {
          options.subtitles.forEach((subtitle, index) => {
            // Remplacer les retours à la ligne par des espaces
            const cleanSubtitle = subtitle.replace(/\n/g, ' ');
            doc.setFontSize(subtitleFontSize);
            doc.text(cleanSubtitle, titlesStartPositionX, offsetYHeader);
            offsetYHeader = offsetYHeader + subtitleUnitHeight;
          });
        }
        //Numéro de page
        doc.setFontSize(pageNumberFontSize);
        doc.text(
          "Page " + doc.page++ + "/" + totalPagesExp,
          pageNumberXStart,
          marginTop - (titleFontSize - pageNumberFontSize) / 2
        );
      }
    };

    if (!tableNumbersWithPrefix) {
      tableNumbersWithPrefix = [{ tableNumbers }];
    }

    tableNumbersWithPrefix.forEach((tableNumberWithPrefix, iGroup) => {
      tableNumberWithPrefix.tableNumbers.forEach((tableNumber, iTable) => {

        const tableId = `#table-reporting-id-${(tableNumberWithPrefix.idPrefix || "")}${tableNumber}`;
        let startY =
          iTable === 0
            ? iGroup === 0
              ? marginTop + headerFinalHeight
              : doc.lastAutoTable.finalY + verticalSpaceBetweenTables
            : doc.lastAutoTable.finalY + verticalSpaceBetweenTables;

        // A chaque début de nouveau groupe de tableaux
        if (iTable === 0 && tableNumberWithPrefix.groupTitle) {
          if (iGroup !== 0) {
            // Break page pour chaque groupe de tableaux
            doc.addPage();
            startY = marginTop + headerFinalHeight + titleHeight;
          } else {
            startY = startY + titleHeight;
          }
          // Ajout du titre du groupe de tableaux
          doc.setFontSize(titleFontSize);
          doc.text(tableNumberWithPrefix.groupTitle, marginLeft, startY);
          startY = startY + verticalSpaceBetweenTables;
        }

        // Vérifier s'il y a assez d'espace pour afficher au moins une ligne de données -> cas où il n'y a que le header + totaux, pas la place pour les données
        const pageHeight = options.orientationPaysage ? 476 : 636;
        const minRequiredSpace = 100;
        const availableSpace = pageHeight - marginTop - startY;

        // Si pas assez d'espace, forcer un saut de page
        if (availableSpace < minRequiredSpace || tableNumber.startsWith('spacer_')) {
          doc.addPage();
          startY = marginTop + headerFinalHeight;
        }

        autoTable(doc, {
          theme: "striped",
          margin: {
            top: marginTop + headerFinalHeight,
            bottom: marginTop,
            left: marginLeft,
            right: marginLeft,
          },
          html: tableId,
          startY: startY,
          didParseCell: (hookData) => { // Gérer les cellules des dimensions pour éviter les débordements
            const hasDimensionClass =
              hookData.cell &&
              hookData.cell.raw &&
              hookData.cell.raw.classList &&
              hookData.cell.raw.classList.contains("export-dimension");

            if (!hasDimensionClass) return;

            const rawText = Array.isArray(hookData.cell.text)
              ? hookData.cell.text.join(" ")
              : String(hookData.cell.text || "");

            if (rawText.length > 20) {
              hookData.cell.styles.cellWidth = maxDimensionCellWidth;
              hookData.cell.styles.overflow = "linebreak";
            }
          },
          didDrawPage: header,
          useCss: true,
        });
      });
    });

    doc.putTotalPages(totalPagesExp);

    return doc;
  },

  /**
   * Return a valid worksheet name from a string, by following https://support.microsoft.com/en-us/office/rename-a-worksheet-3f1f7148-ee83-404d-8ef0-9ff99fbad1f9
   * @param {string} sheetName
   */
  getValidSheetName(sheetName) {
    let result = sheetName;
    if (sheetName.toLowerCase() === 'history')
      return '_' + sheetName
    if (sheetName.length > 31)
      result = result.slice(0, 31)
    //On enlève tous les caractères interdits
    result = result.replaceAll(/\?|\*/g, '.')
      .replaceAll(/\/|\\|:/g, '-')
      .replaceAll('[', '(')
      .replaceAll(']', ')')
      .replaceAll(/^'|'$/g, '"')
    return result;
  }
};
